import React, { useState } from 'react';
import { GameType, ViewType } from '../types';
import { Play, Pause, Volume2, VolumeX, Sparkles, Flame, Trophy, QrCode, ArrowRight, Lightbulb, RefreshCw, Zap } from 'lucide-react';

interface HomeViewProps {
  onNavigate: (view: ViewType) => void;
  onOpenGame: (game: GameType, mode?: 'normal' | 'competition') => void;
  onOpenLeaderboard: (game?: 'tower' | 'competition' | 'draw' | 'puzzle') => void;
  onScrollToGame?: (game: 'tower' | 'draw' | 'puzzle') => void;
  isVideoPlaying: boolean;
  onToggleVideo: () => void;
  isMuted: boolean;
  onToggleSound: () => void;
  videoRef: React.RefObject<HTMLVideoElement | null>;
}

const ART_FUN_FACTS = [
  '🎨 Tranh sơn mài Việt Nam độc nhất vô nhị vì dùng bột vỏ trứng, vàng quỳ và mài dưới nước!',
  '🌟 Tác phẩm "Em Thúy" của danh họa Trần Văn Cẩn từng suýt bị hỏng nặng và được chuyên gia nước ngoài phục chế kỳ công.',
  '🏆 Bức "Kết nạp Đảng ở Điện Biên Phủ" được công nhận là Bảo vật Quốc gia vì tái hiện giây phút lịch sử hào hùng.',
  '💡 Bạn có biết: Tượng Phật Bà Quan Âm nghìn mắt nghìn tay có tới 42 cánh tay lớn và 952 cánh tay nhỏ?',
  '🏰 Tháp trí tuệ có tới 10 tầng câu đố — Chỉ 5% người chơi chạm tới đỉnh tháp mà không mất mạng nào!',
];

export const HomeView: React.FC<HomeViewProps> = ({
  onNavigate,
  onOpenGame,
  onOpenLeaderboard,
  onScrollToGame,
  isVideoPlaying,
  onToggleVideo,
  isMuted,
  onToggleSound,
  videoRef,
}) => {
  const [factIndex, setFactIndex] = useState(0);

  const nextFact = () => {
    setFactIndex((prev) => (prev + 1) % ART_FUN_FACTS.length);
  };

  const scrollToPlay = () => {
    const el = document.getElementById('play-now');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToGame = (game: 'tower' | 'draw' | 'puzzle') => {
    if (onScrollToGame) {
      onScrollToGame(game);
    } else {
      const targetId = `game-${game}`;
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('ring-4', 'ring-[#ffd166]', 'shadow-[0_0_30px_rgba(255,209,102,0.6)]');
        setTimeout(() => el.classList.remove('ring-4', 'ring-[#ffd166]', 'shadow-[0_0_30px_rgba(255,209,102,0.6)]'), 2000);
      }
    }
  };

  return (
    <main className="world">
      {/* Skip link for accessibility */}
      <a className="skip-link" href="#play-now">
        Tới các trò chơi 🎮
      </a>

      {/* HERO FRAME */}
      <section className="hero-frame" aria-labelledby="world-title">
        {/* Background scene */}
        <div className="world-scene" aria-hidden="true">
          <img
            className="world-photo"
            src="/images/generated/heritage-carmine-background-v1.webp"
            alt="Mỹ thuật Việt Nam di sản"
            width="1600"
            height="860"
          />
          <video
            ref={videoRef}
            id="hero-video"
            className="world-video"
            autoPlay
            loop
            muted={isMuted}
            playsInline
            poster="/images/generated/heritage-carmine-background-v1.webp"
          >
            <source src="/video/vnfam-v2-hero-museum-journey.mp4" type="video/mp4" />
          </video>
          <div className="world-shade" />
        </div>

        {/* Video Controls: Nút tròn điều khiển âm thanh & video */}
        <div className="absolute top-[72px] sm:top-[94px] right-3 sm:right-8 z-20 flex items-center gap-1.5 sm:gap-2">
          <button
            type="button"
            id="hero-video-toggle"
            onClick={onToggleVideo}
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black/75 hover:bg-black text-[#fff8ee] border-2 border-[#ffd166]/60 hover:border-[#ffd166] flex items-center justify-center shadow-lg backdrop-blur-md active:scale-90 transition-all cursor-pointer"
            aria-pressed={isVideoPlaying}
            aria-label={isVideoPlaying ? 'Tạm dừng video nền' : 'Tiếp tục phát video nền'}
            title={isVideoPlaying ? 'Tạm dừng video' : 'Tiếp tục phát video'}
          >
            {isVideoPlaying ? (
              <Pause className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#ffd166] fill-current" />
            ) : (
              <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#06d6a0] fill-current ml-0.5" />
            )}
          </button>

          <button
            type="button"
            id="hero-sound-toggle"
            onClick={onToggleSound}
            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shadow-lg backdrop-blur-md active:scale-90 transition-all cursor-pointer border-2 ${
              !isMuted
                ? 'bg-[#063a2e]/90 hover:bg-[#084c3c] text-[#06d6a0] border-[#06d6a0] shadow-[0_0_16px_rgba(6,214,160,0.4)]'
                : 'bg-black/75 hover:bg-black text-[#fce4ce]/80 hover:text-white border-[#ffd166]/60 hover:border-[#ffd166]'
            }`}
            aria-pressed={!isMuted}
            aria-label={isMuted ? 'Bật âm thanh video' : 'Tắt âm thanh video'}
            title={isMuted ? 'Bật âm thanh' : 'Tắt âm thanh'}
          >
            {!isMuted ? (
              <Volume2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#06d6a0] animate-pulse" />
            ) : (
              <VolumeX className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#fce4ce]/80" />
            )}
          </button>
        </div>

        {/* Hero Intro Content */}
        <div className="world-main">
          <div className="world-intro space-y-3 sm:space-y-4">
            {/* Playful Eyebrow */}
            <div className="chapter">
              <span /> 🎮 ĐẤU TRƯỜNG MỸ THUẬT TEEN · VNFAM ⚡
            </div>

            {/* Energetic Teen Headline */}
            <h1 id="world-title">
              Phá Đảo Bảo Tàng,
              <br />
              Săn Trọn <em>Kiệt Tác!</em> 🚀
            </h1>

            {/* Engaging Subtitle */}
            <p className="world-description">
              Biến chuyến tham quan thành cuộc phiêu lưu kịch tính! Vượt 10 tầng tháp trí tuệ, so tài nét vẽ lầy lội và quét mã QR săn trọn 9 Bảo vật Quốc gia.
            </p>

            {/* Sleek Compact Mascot Tip Pill */}
            <div className="pt-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#281333]/90 border border-[#ffd166]/40 shadow-lg backdrop-blur-md max-w-full text-xs text-[#fce4ce]">
                <span className="text-sm select-none shrink-0">🖌️</span>
                <span className="font-black text-[#ffd166] shrink-0 text-[11px] sm:text-xs">Bé Cọ mách:</span>
                <span className="truncate text-[#fff8ee] text-[11.5px] sm:text-xs max-w-[240px] sm:max-w-md">
                  {ART_FUN_FACTS[factIndex]}
                </span>
                <button
                  type="button"
                  onClick={nextFact}
                  className="text-[#ffd166]/80 hover:text-white p-0.5 rounded transition-transform active:rotate-180 shrink-0 ml-0.5"
                  title="Đổi mẹo khác"
                >
                  <RefreshCw className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Hero Quick Action Buttons */}
            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <button
                type="button"
                onClick={scrollToPlay}
                className="gf-button text-xs sm:text-sm py-3 px-6 shadow-xl flex items-center justify-center gap-2"
              >
                <span>🎮 Vào Chơi Ngay</span>
                <ArrowRight className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={() => onNavigate('journey')}
                className="gf-button gf-button-secondary text-xs sm:text-sm py-3 px-5 flex items-center justify-center gap-2"
              >
                <QrCode className="w-4 h-4 text-[#ffd166]" />
                <span>🎒 Săn Tem QR Bảo Tàng</span>
              </button>
            </div>

            {/* Micro Badges */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] sm:text-xs font-bold text-[#fce4ce]/70 pt-1">
              <span>🏛️ 9 Bảo vật Quốc gia</span>
              <span className="text-[#ffd166]/40">•</span>
              <span>🔥 Đua Top Hàng Tuần</span>
              <span className="text-[#ffd166]/40">•</span>
              <span>🏆 Nhận Cúp Bảng Vàng</span>
            </div>
          </div>
        </div>

        {/* Subtle Scroll Down Indicator */}
        <button
          type="button"
          onClick={scrollToPlay}
          className="absolute bottom-5 sm:bottom-7 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1.5 text-[#ffd166]/75 hover:text-[#ffd166] transition-colors cursor-pointer group"
          aria-label="Cuộn xuống khu vực trò chơi"
        >
          <span className="text-[9.5px] sm:text-[10px] font-black uppercase tracking-widest text-[#ffd166]/80 group-hover:text-[#ffd166]">
            Khám phá 3 Trò chơi
          </span>
          <span className="w-6 h-6 rounded-full border border-[#ffd166]/40 flex items-center justify-center text-xs animate-bounce group-hover:border-[#ffd166]">
            ↓
          </span>
        </button>
      </section>

      {/* 3 GAMES CHALLENGE FRAME */}
      <section className="challenge-frame scroll-mt-24 md:scroll-mt-28" id="play-now" aria-labelledby="play-title">
        <div className="challenge-frame__head">
          <p>🎮 PHÒNG GAME VNFAM ARCADE · MIỄN PHÍ</p>
          <h2 id="play-title">Chọn thử thách của bạn</h2>
          <span>⚡ 03 TRÒ CHƠI ĐANG MỞ · CHƠI LÀ MÊ!</span>
        </div>

        {/* Featured Weekly Tournament Banner */}
        <article className="weekly-banner mb-6">
          <img
            src="/images/artifacts/giong-nguyen-tu-nghiem.webp"
            alt="Tác phẩm Gióng"
          />
          <div className="weekly-copy">
            <p>
              <span className="live-dot" /> 🔥 GIẢI ĐẤU TUẦN SÔI ĐỘNG{' '}
              <span className="sample-label">MÙA 1</span>
            </p>
            <h2>Đua Top Tháp Trí Tuệ</h2>
            <span>Chinh phục 10 tầng câu đố mỹ thuật · Săn Cúp Chiến Thần Bảng Vàng</span>
            <div className="weekly-actions">
              <button
                type="button"
                onClick={() => onOpenGame('tower', 'competition')}
              >
                <span>⚔️ Vào Đua Ngay</span>
                <span aria-hidden="true">↗</span>
              </button>
              <button
                id="weekly-leaderboard"
                type="button"
                onClick={() => onOpenLeaderboard('competition')}
              >
                <Trophy className="w-3.5 h-3.5 text-[#ffd166]" />
                <span>BXH Top 100</span>
              </button>
            </div>
          </div>
        </article>

        <aside className="play-rail" aria-label="Ba thử thách đang mở">
          {/* Game 1: Leo tháp */}
          <article className="play-item">
            <button
              id="game-tower"
              className="play-banner tower-banner scroll-mt-24"
              type="button"
              onClick={() => onOpenGame('tower', 'normal')}
              aria-label="Chơi Leo tháp mỹ thuật — chế độ thường"
              aria-describedby="game-tower-description"
            >
              <img
                src="/images/generated/game-tower-puzzle-realistic-v1.webp"
                alt="Người chơi giải đố trong phòng trưng bày"
              />
              <span className="banner-overlay" />
              <span className="banner-copy">
                <small>🏰 01 / HACK NÃO LEO THÁP</small>
                <strong>
                  Leo tháp
                  <br />
                  mỹ thuật
                </strong>
                <span>
                  CHINH PHỤC 10 TẦNG <i aria-hidden="true">↗</i>
                </span>
              </span>
              <span className="play-arrow" aria-hidden="true">
                ↗
              </span>
            </button>
            <div className="play-description" id="game-tower-description">
              <span className="play-description__body">
                <span className="play-description__eyebrow">🔥 BẢN LĨNH NGƯỜI LEO THÁP</span>
                <strong>Mỗi tầng 1 câu đố hack não — Giữ trọn 3 tim!</strong>
                <span className="play-description__copy">Vượt qua các câu hỏi trắc nghiệm, sắp xếp niên đại và ghép tranh. Nhận bùa may mắn từ Vòng Quay Thần Bí để chạm nóc tháp rinh cúp Bảng Vàng!</span>
                <span className="play-description__meta">
                  <b>❤️ 3 Trái Tim</b>
                  <i />
                  <b>🎡 Vòng Quay May Mắn</b>
                  <i />
                  <b>🏆 Top 100 BXH</b>
                </span>
              </span>
            </div>
          </article>

          {/* Game 2: Vẽ & đoán */}
          <article className="play-item">
            <button
              id="game-draw"
              className="play-banner draw-banner scroll-mt-24"
              type="button"
              onClick={() => onOpenGame('draw')}
              aria-label="Chơi Vẽ và đoán"
              aria-describedby="game-draw-description"
            >
              <img
                src="/images/generated/game-draw-guess-realistic-v1.webp"
                alt="Trải nghiệm vẽ và đoán tại bảo tàng"
              />
              <span className="banner-overlay" />
              <span className="banner-copy">
                <small>🎨 02 / HỌA SĨ BẤT ĐẮC DĨ</small>
                <strong>Vẽ & đoán tác phẩm</strong>
                <span>
                  VẼ NÉT LẦY LỘI <i aria-hidden="true">↗</i>
                </span>
              </span>
              <span className="play-arrow" aria-hidden="true">
                ↗
              </span>
            </button>
            <div className="play-description" id="game-draw-description">
              <span className="play-description__body">
                <span className="play-description__eyebrow">✨ XƯỞNG VẼ BẤT NGỜ</span>
                <strong>Không cần vẽ đẹp, chỉ cần vẽ... đủ hiểu!</strong>
                <span className="play-description__copy">Dùng bảng màu sơn mài cổ truyền phác vài nét gợi ý để bạn bè đoán tác phẩm. Thỏa sức cười nghiêng ngả và rinh điểm chuỗi combo siêu khủng!</span>
                <span className="play-description__meta">
                  <b>🖌️ Bảng Màu Cổ Truyền</b>
                  <i />
                  <b>⏳ 30s Đoán Nhanh</b>
                  <i />
                  <b>🔥 Điểm Combo</b>
                </span>
              </span>
            </div>
          </article>

          {/* Game 3: Ghép tranh */}
          <article className="play-item">
            <button
              id="game-puzzle"
              className="play-banner puzzle-banner scroll-mt-24"
              type="button"
              onClick={() => onOpenGame('puzzle')}
              aria-label="Chơi Ghép tranh"
              aria-describedby="game-puzzle-description"
            >
              <img
                src="/images/generated/game-jigsaw-realistic-v1.webp"
                alt="Người chơi ghép những mảnh tranh"
              />
              <span className="banner-overlay" />
              <span className="banner-copy">
                <small>🧩 03 / THÁM TỬ PHỤC CHẾ</small>
                <strong>Ghép tranh di sản</strong>
                <span>
                  RÁP MẢNH BẢO VẬT <i aria-hidden="true">↗</i>
                </span>
              </span>
              <span className="play-arrow" aria-hidden="true">
                ↗
              </span>
            </button>
            <div className="play-description" id="game-puzzle-description">
              <span className="play-description__body">
                <span className="play-description__eyebrow">⚡ PHÒNG PHỤC CHẾ KIỆT TÁC</span>
                <strong>Bảo vật bị vỡ! Nhanh tay ráp lại kiệt tác!</strong>
                <span className="play-description__copy">Kéo và đổi chỗ các mảnh ghép để khôi phục kiệt tác trước khi đồng hồ điểm 00s. Thử sức từ bàn 2×2 cho lính mới đến 4×4 cho thánh tốc độ!</span>
                <span className="play-description__meta">
                  <b>🧩 2×2 / 3×3 / 4×4</b>
                  <i />
                  <b>⏱️ Đua Giây Phục Chế</b>
                  <i />
                  <b>⭐ 6 Kiệt Tác Hiếm</b>
                </span>
              </span>
            </div>
          </article>
        </aside>

        {/* Real World Museum Quest Banner */}
        <div className="mt-8 p-5 sm:p-6 rounded-2xl sm:rounded-3xl border-2 border-[#ffd166]/50 bg-gradient-to-br from-[#3b1528] via-[#241122] to-[#1a0e1c] shadow-xl flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#ffd166] to-[#ff3366] flex items-center justify-center text-2xl shadow-lg shrink-0">
              🎒
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-[#ffd166]">Nhiệm Vụ Thực Địa Tại Bảo Tàng</span>
              <h3 className="text-base sm:text-lg font-black text-white">Săn Tem 9 Bảo Vật Quốc Gia Trực Tiếp</h3>
              <p className="text-xs text-[#fce4ce]/80">Ghé thăm 66 Nguyễn Thái Học, Hà Nội · Quét mã QR dưới các bức tranh để mở khóa cốt truyện bí mật!</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onNavigate('journey')}
            className="gf-button text-xs sm:text-sm py-2.5 px-5 shrink-0 flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            <QrCode className="w-4 h-4 text-[#ffd166]" />
            <span>Mở Balo Săn Tem</span>
          </button>
        </div>
      </section>

      {/* World Footer */}
      <footer className="world-footer">
        <span className="flex items-center gap-2">
          <span>🏛️ BẢO TÀNG MỸ THUẬT VIỆT NAM · 66 NGUYỄN THÁI HỌC, HÀ NỘI</span>
        </span>
        <span className="footer-rule" />
        <span className="text-[#ffd166]">VNFAM Play Edition · Chơi Là Mê ✨</span>
      </footer>
    </main>
  );
};
